import { IS_DEV } from '../viteEnv';
import styled from '@emotion/styled';
import { Button, Tooltip } from '@material-ui/core';
import { compact } from 'lodash';
import React, { useMemo } from 'react';
import { isMobile } from 'react-device-detect';
import { Link } from 'react-router-dom';
import { DadoLogo, DadoLogoWithName } from '../components/DadoLogo';
import { LinkExternal } from '../components/LinkExternal';
import ENV from '../env.json';
import { usePromise } from '../hooks/usePromise';
import { useTranslation } from '../i18next';
import { useRoutes } from '../routes';
import { checkAdminClaim } from '../services/auth';
import { useCurrentUser } from '../services/currentUser';
import { getFirstAccessibleRoute } from '../services/navigation';
import { isFreeTierOrg } from '../services/organizationTier';
import { pathnameWithoutQueryParams } from '../services/url';
import { SIDE_NAV_WIDTH } from './PageBody';
import { TopNavRight } from './TopNavRight';

export const GoToAdminApp = () => {
  const currentUser = useCurrentUser();
  const [isAdmin] = usePromise(
    async () => currentUser.authUser && checkAdminClaim(currentUser.authUser),
    [currentUser.authUser]
  );
  if (!isAdmin) {
    return null;
  }

  const p = ENV.domains.adminApp;
  const domain = IS_DEV ? p.local : p.public;

  return (
    <a
      rel="noopener noreferrer"
      target="_blank"
      href={`${domain}/orgs/${currentUser.org.id}`}
    >
      Go to Admin App
    </a>
  );
};

export const TOP_NAV = {
  COLORS: {
    light: '#ffffff',
    lightDimmed: '#ffffffde',
    dark: '#111111'
  }
};

const TopNavBar = styled('div')`
  top: 0;
  position: sticky;
  display: flex;
  align-items: center;
  padding: ${(p) => `${p.theme.spacing(1.25)}px ${p.theme.spacing(2)}px`};
  color: ${TOP_NAV.COLORS.light};
  background-color: ${(p) => p.theme.custom.colors.blue.w700};
  z-index: 100;

  @media (min-width: 600px) {
    padding-left: ${(p) => p.theme.spacing(4)}px;
    padding-right: ${(p) => p.theme.spacing(4)}px;
  }

  @media (min-width: 1600px) {
    padding-left: ${(p) => p.theme.spacing(6)}px;
    padding-right: ${(p) => p.theme.spacing(6)}px;
  }
`;

const TopNavBarSection = styled('div')<{
  flex: number;
  align?: string;
  minWidth?: number;
  display?: string;
  alignItems?: string;
}>`
  display: ${(p) => p.display};
  align-items: ${(p) => p.alignItems};
  flex: ${(p) => p.flex};
  text-align: ${(p) => p.align || 'left'};
  ${(p) => (p.minWidth ? `min-width: ${p.minWidth}px;` : '')};
`;

const TopNavLink = styled('span')<{ selected?: boolean; lastChild?: boolean }>`
  :not(:last-child) {
    margin-right: ${(p) => p.theme.spacing(4)}px;
  }
  :last-child {
    margin-right: ${(p) => (!p.lastChild ? 0 : p.theme.spacing(2))}px;
  }
  ${(p) =>
    p.selected ? `font-weight: ${p.theme.typography.fontWeightMedium};` : ''}

  @media (max-width: 1050px) {
    display: ${(p) => (p.lastChild ? 'none' : 'flex')};
  }
`;

export const TopNav = React.memo(() => {
  const { ROUTES, getPath } = useRoutes();
  const { t } = useTranslation();
  const user = useCurrentUser();
  const freeTier = isFreeTierOrg(user.org);
  const items = useMemo(
    () =>
      compact(
        [
          !freeTier && {
            label: t('topNav.experiences'),
            path: ROUTES.experiences.overview.url()
          },
          {
            label: t('topNav.moments'),
            path: ROUTES.moments.overview.url()
          },
          {
            label: t('topNav.reports'),
            path: ROUTES.reports.overview.url(),
            comingSoon: false
          },
          freeTier && {
            label: t('topNav.employees'),
            path: ROUTES.users.standalone.url()
          },
          !freeTier && {
            label: t('topNav.users'),
            path: getFirstAccessibleRoute(user, [
              ROUTES.users.overview,
              ROUTES.users.imports.overview
            ])?.url()
          },
          {
            label: t('topNav.organization'),
            path: getFirstAccessibleRoute(user, [
              ROUTES.org,
              ROUTES.org.customFields,
              ROUTES.org.integrations
            ])?.url()
          }
        ]
          // For paths that require specific permissions, check whether they are resolved.
          // If the path is `null`, the user can’t access any of the links, so we filter those out.
          .filter((r) => r && r.path)
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ROUTES, user]
  );

  const currentPath = getPath();
  const selected = useMemo(() => {
    return items.find((x) => {
      const pathname = pathnameWithoutQueryParams(x.path);
      const p = pathname.split('/')[1];
      const c = currentPath.split('/')[1];
      return p === c;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPath, items]);
  return (
    <TopNavBar>
      <TopNavBarSection flex={0} minWidth={isMobile ? 0 : SIDE_NAV_WIDTH}>
        <Link to={ROUTES.experiences.overview.url()}>
          {isMobile ? (
            <DadoLogo size={24} />
          ) : (
            <DadoLogoWithName variant="reverse" size={20} />
          )}
        </Link>
      </TopNavBarSection>
      {!isMobile && (
        <TopNavBarSection flex={1} display="flex" alignItems="center">
          {items.map((x) =>
            x.comingSoon ? (
              <Tooltip key={x.label} title={`${t('Coming soon')}!`}>
                <TopNavLink selected={x === selected}>{x.label}</TopNavLink>
              </Tooltip>
            ) : (
              <TopNavLink key={x.label} selected={x === selected}>
                <Link to={x.path}>{x.label}</Link>
              </TopNavLink>
            )
          )}
          <TopNavLink lastChild>
            <Button variant="contained" color="primary" size="small">
              <LinkExternal
                href={'https://www.g2.com/products/dadohr/reviews/start'}
              >
                {t('Review us on G2')}
              </LinkExternal>
            </Button>
          </TopNavLink>
        </TopNavBarSection>
      )}
      <TopNavBarSection flex={1} align="right">
        <TopNavRight />
      </TopNavBarSection>
    </TopNavBar>
  );
});

const MultiLogo = styled('div')((p) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: p.theme.spacing()
}));

const PlusContainer = styled('div')`
  font-size: ${(p) => p.theme.custom.fontSize.xl}px;
  font-weight: ${(p) => p.theme.typography.fontWeightBold};
  line-height: 1;
`;

export const TopNavBarLogoOnly = ({
  additionalLogoUrl,
  additionalLogoAlt
}: {
  additionalLogoUrl?: string;
  additionalLogoAlt?: string;
}) => (
  <TopNavBar>
    <TopNavBarSection flex={0}>
      <MultiLogo>
        <DadoLogoWithName size={24} variant="reverse" />
        {additionalLogoUrl && (
          <>
            <PlusContainer>+</PlusContainer>
            <img src={additionalLogoUrl} height={24} alt={additionalLogoAlt} />
          </>
        )}
      </MultiLogo>
    </TopNavBarSection>
  </TopNavBar>
);
